import { createContext, ReactNode, useContext, useMemo } from "react";
import { AdressesFavoritesService } from "../services/adresses-favorites-service";
import { AdresseService } from "../services/adresse-service";
import { ApprobationEntrepriseService } from "../services/approbation-entreprise-service";
import { ClientService } from "../services/client-service";
import { VehicleService } from "../services/vehicle-service";
import { DriverService } from "../services/driver-service";
import { EntrepriseService } from "../services/entreprise-service";
import { FactureService } from "../services/facture-service";
import { InvitationEntrepriseService } from "../services/invitation-entreprise-service";
import { CourseService } from "../services/course-service";
import { TarifFixeService } from "../services/tarif-fixe-service";
import { UserService } from "../services/user-service";
import { ContactService } from "../services/contact-service";
import { ConversationService } from "../services/conversation-service";
import { NotificationService } from "../services/notification-service";

type ServiceKmsContextType = {
  userService: UserService;
  adressesFavoritesService: AdressesFavoritesService;
  adresseService: AdresseService;
  approbationEntrepriseService: ApprobationEntrepriseService;
  clientService: ClientService;
  driverService: DriverService;
  entrepriseService: EntrepriseService;
  factureService: FactureService;
  invitationEntrepriseService: InvitationEntrepriseService;
  courseService: CourseService;
  tarifFixeService: TarifFixeService;
  vehicleService: VehicleService;
  contactService: ContactService;
  conversationService: ConversationService;
  notificationService: NotificationService;
};

const KmsServiceContext = createContext<ServiceKmsContextType | null>(null);

const apiUrl = "/backend-api";

export const KmsServiceProvider = ({ children }: { children: ReactNode }) => {
  // L'authentification est portée par le cookie HttpOnly `keycloak-token`,
  // envoyé automatiquement aux appels same-origin vers /backend-api. Le proxy
  // serveur y injecte l'en-tête Authorization ; les services n'ont donc plus
  // besoin de l'access token.
  const services = useMemo(
    () => ({
      userService: new UserService(apiUrl),
      adressesFavoritesService: new AdressesFavoritesService(apiUrl),
      adresseService: new AdresseService(apiUrl),
      approbationEntrepriseService: new ApprobationEntrepriseService(apiUrl),
      clientService: new ClientService(apiUrl),
      driverService: new DriverService(apiUrl),
      entrepriseService: new EntrepriseService(apiUrl),
      factureService: new FactureService(apiUrl),
      invitationEntrepriseService: new InvitationEntrepriseService(apiUrl),
      courseService: new CourseService(apiUrl),
      tarifFixeService: new TarifFixeService(apiUrl),
      vehicleService: new VehicleService(apiUrl),
      contactService: new ContactService(apiUrl),
      conversationService: new ConversationService(apiUrl),
      notificationService: new NotificationService(apiUrl),
    }),
    [],
  );

  return (
    <KmsServiceContext.Provider value={services}>
      {children}
    </KmsServiceContext.Provider>
  );
};

export const useKmsService = () => {
  const services = useContext(KmsServiceContext);

  if (!services) {
    throw new Error("useIoService must be used within ServiceElectronProvider");
  }

  return services;
};

export const useCourse = () => useKmsService().courseService;
